/**
 * Google Maps URL utilities for creating search and location links
 * Based on Google Maps URL API: https://developers.google.com/maps/documentation/urls/get-started
 */
import { logger } from './logger';

/**
 * Singleton state for Google Maps script loading
 */
let googleMapsLoadPromise: Promise<void> | null = null;

/**
 * Polling interval for checking importLibrary availability after script load
 * Google Maps API needs a moment to initialize importLibrary after script loads
 */
const IMPORT_LIBRARY_POLL_INTERVAL_MS = 50;

/**
 * Maximum time to wait for importLibrary to become available after script loads
 */
const IMPORT_LIBRARY_TIMEOUT_MS = 5000;

function hasImportLibrary(): boolean {
  const maybeGoogle = (window as Window & { google?: { maps?: { importLibrary?: unknown } } })
    .google;
  return typeof maybeGoogle?.maps?.importLibrary === 'function';
}

/**
 * Poll for importLibrary availability after script loads
 * Google Maps API needs a moment to initialize importLibrary after the script tag loads
 *
 * @param resolve - Promise resolve function to call when importLibrary is available
 * @param reject - Promise reject function to call on timeout
 * @returns Cleanup function to cancel polling
 */
function pollForImportLibrary(resolve: () => void, reject: (error: Error) => void): () => void {
  const startTime = Date.now();
  let timeoutId: ReturnType<typeof setTimeout> | null = null;
  let cancelled = false;

  const checkImportLibrary = () => {
    if (cancelled) return;

    if (hasImportLibrary()) {
      resolve();
    } else if (Date.now() - startTime < IMPORT_LIBRARY_TIMEOUT_MS) {
      timeoutId = setTimeout(checkImportLibrary, IMPORT_LIBRARY_POLL_INTERVAL_MS);
    } else {
      reject(
        new Error(
          `Google Maps script loaded but importLibrary not available after ${IMPORT_LIBRARY_TIMEOUT_MS}ms. This may indicate a network issue, an incompatible API version, or a configuration problem with the Google Maps API key.`,
        ),
      );
    }
  };

  checkImportLibrary();

  // Return cleanup function
  return () => {
    cancelled = true;
    if (timeoutId !== null) {
      clearTimeout(timeoutId);
      timeoutId = null;
    }
  };
}

/**
 * Load Google Maps JavaScript API script with singleton pattern using Dynamic Library Import
 * Prevents duplicate script tags and ensures script loads only once
 * Libraries are loaded on-demand using window.google.maps.importLibrary()
 *
 * @param apiKey - Google Maps API key
 * @returns Promise that resolves when window.google.maps is available
 *
 * @example
 * await loadGoogleMapsScript(import.meta.env.VITE_GOOGLE_PLACES_API_KEY);
 * // Now window.google.maps.importLibrary is available
 * const { AutocompleteSuggestion } = await window.google.maps.importLibrary('places');
 */
export function loadGoogleMapsScript(apiKey: string): Promise<void> {
  // Return existing promise if already loading or loaded
  if (googleMapsLoadPromise) {
    return googleMapsLoadPromise;
  }

  // Check if already loaded with importLibrary support
  if (hasImportLibrary()) {
    googleMapsLoadPromise = Promise.resolve();
    return googleMapsLoadPromise;
  }

  // Check if script tag already exists
  const existingScript = document.querySelector('script[src*="maps.googleapis.com/maps/api/js"]');
  if (existingScript) {
    googleMapsLoadPromise = new Promise<void>((resolve, reject) => {
      // Check if already loaded with importLibrary support
      if (hasImportLibrary()) {
        resolve();
        return;
      }

      // Set up timeout first
      const timeoutId = setTimeout(() => {
        if (cancelPolling) cancelPolling();
        existingScript.removeEventListener('load', handleLoad);
        existingScript.removeEventListener('error', handleError);
        reject(new Error('Google Maps script load timeout'));
        queueMicrotask(() => {
          googleMapsLoadPromise = null;
        });
      }, 10000);

      // Track polling cleanup
      let cancelPolling: (() => void) | null = null;

      // Event handlers
      const handleLoad = () => {
        clearTimeout(timeoutId);
        existingScript.removeEventListener('error', handleError);
        // Poll for importLibrary availability (may take a moment after script loads)
        cancelPolling = pollForImportLibrary(resolve, error => {
          reject(error);
          queueMicrotask(() => {
            googleMapsLoadPromise = null;
          });
        });
      };

      const handleError = () => {
        clearTimeout(timeoutId);
        if (cancelPolling) cancelPolling();
        existingScript.removeEventListener('load', handleLoad);
        reject(new Error('Failed to load Google Maps script'));
        // Use queueMicrotask to reset the promise AFTER the rejection propagates.
        // This ensures all currently awaiting callers receive the rejection before we reset.
        // Without this timing, immediate reset could cause race conditions where subsequent
        // callers get a fresh promise while earlier callers are still handling the rejection.
        queueMicrotask(() => {
          googleMapsLoadPromise = null;
        });
      };

      // Attach event listeners
      existingScript.addEventListener('load', handleLoad);
      existingScript.addEventListener('error', handleError);
    });
    return googleMapsLoadPromise;
  }

  // Create and load new script
  googleMapsLoadPromise = new Promise<void>((resolve, reject) => {
    const script = document.createElement('script');
    // Use Dynamic Library Import API - don't specify libraries, use importLibrary() instead
    script.src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}&loading=async`;
    script.async = true;
    script.defer = true;

    // Track polling cleanup
    let cancelPolling: (() => void) | null = null;

    // Set up timeout to prevent indefinite waiting
    const timeoutId = setTimeout(() => {
      if (cancelPolling) cancelPolling();
      script.onload = null;
      script.onerror = null;
      reject(new Error('Google Maps script load timeout'));
      queueMicrotask(() => {
        googleMapsLoadPromise = null;
      });
    }, 10000);

    script.onload = () => {
      clearTimeout(timeoutId);
      // Poll for importLibrary availability (may take a moment after script loads)
      cancelPolling = pollForImportLibrary(resolve, error => {
        reject(error);
        queueMicrotask(() => {
          googleMapsLoadPromise = null;
        });
      });
    };

    script.onerror = () => {
      clearTimeout(timeoutId);
      if (cancelPolling) cancelPolling();
      reject(new Error('Failed to load Google Maps script'));
      // Use queueMicrotask to reset the promise AFTER the rejection propagates.
      // This ensures all currently awaiting callers receive the rejection before we reset.
      // Without this timing, immediate reset could cause race conditions where subsequent
      // callers get a fresh promise while earlier callers are still handling the rejection.
      queueMicrotask(() => {
        googleMapsLoadPromise = null;
      });
    };

    document.head.appendChild(script);
  });

  return googleMapsLoadPromise;
}

/**
 * Generate a Google Maps search URL using the place name/address
 * This opens Google Maps with a search for the specified location
 *
 * @param locationName - The place name or address to search for
 * @returns Google Maps search URL
 */
export function generateGoogleMapsSearchUrl(locationName: string): string {
  if (!locationName.trim()) {
    return '';
  }

  // URL encode the location name for proper formatting
  const encodedLocation = encodeURIComponent(locationName.trim());

  // Use Google Maps search API format
  return `https://www.google.com/maps/search/?api=1&query=${encodedLocation}`;
}

/**
 * Generate a Google Maps URL using coordinates
 * This centers the map on the exact coordinates
 *
 * The tuple is LONGITUDE FIRST (LocationData.coordinates order); Google's `query` param is
 * latitude first, so the two are deliberately swapped below. Order is pinned by
 * `src/utils/__tests__/googleMaps.test.ts` — a flip here is invisible in a diff, and both values
 * pass every range check anywhere |lat| and |lng| are under 90. No caller today, so the test is
 * what keeps the order honest until one exists.
 *
 * @param coordinates - [longitude, latitude] coordinate pair
 * @returns Google Maps coordinate URL
 */
export function generateGoogleMapsCoordinateUrl(coordinates: [number, number]): string {
  const [lng, lat] = coordinates;
  return `https://www.google.com/maps/search/?api=1&query=${lat},${lng}`;
}

/**
 * Open Google Maps in a new tab with the specified location
 * Uses search format for better user experience
 *
 * @param locationName - The place name or address to search for
 */
export function openLocationInGoogleMaps(locationName: string): void {
  if (!locationName.trim()) {
    logger.warn('No location provided to open in Google Maps');
    return;
  }

  const url = generateGoogleMapsSearchUrl(locationName);
  window.open(url, '_blank', 'noopener,noreferrer');
}
